import axios from "../../api/axios";
import { toast } from 'react-toastify';

// Vehicle and equipment service trackers.
const BASE = "/v2/service-tracker";

const fail = (error, fallback) => {
  const message = error.response?.data?.message || error.message || fallback;
  console.error(fallback, error);
  toast.error(message);
  throw new Error(message);
};

// The server asks before saving a km lower than the vehicle already shows.
// That is a question for the user, not an error: pass it up untoasted.
const lowerKm = (error) => {
  if (error.response?.status === 409 && error.response?.data?.code === 'LOWER_KM') {
    const e = new Error(error.response.data.message);
    e.lowerKm = true;
    return e;
  }
  return null;
};

/** { kind, propertyId?, buildingName?, year? } -> { data: assets, canManage, canRecord } */
export const getAssets = async (params) => {
  try {
    return (await axios.get(`${BASE}/assets`, { params })).data;
  } catch (error) {
    return fail(error, "Failed to load the tracker");
  }
};

export const saveAsset = async (id, payload) => {
  try {
    const response = id ? await axios.patch(`${BASE}/assets/${id}`, payload) : await axios.post(`${BASE}/assets`, payload);
    return response.data;
  } catch (error) {
    return fail(error, "Failed to save");
  }
};

export const deleteAsset = async (id) => {
  try {
    return (await axios.delete(`${BASE}/assets/${id}`)).data;
  } catch (error) {
    return fail(error, "Failed to delete");
  }
};

export const updateVehicleKm = async (id, currentKm, allowLowerKm = false) => {
  try {
    return (await axios.patch(`${BASE}/assets/${id}/km`, { currentKm, allowLowerKm })).data;
  } catch (error) {
    const question = lowerKm(error);
    if (question) throw question;
    return fail(error, "Failed to update km");
  }
};

export const getAssetTasks = async (id) => {
  try {
    return (await axios.get(`${BASE}/assets/${id}/tasks`)).data.data || [];
  } catch (error) {
    return fail(error, "Failed to load the history");
  }
};

export const saveTask = async (assetId, taskId, payload) => {
  try {
    const response = taskId ? await axios.patch(`${BASE}/tasks/${taskId}`, payload) : await axios.post(`${BASE}/assets/${assetId}/tasks`, payload);
    return response.data;
  } catch (error) {
    return fail(error, "Failed to save the service");
  }
};

export const deleteTask = async (taskId) => {
  try {
    return (await axios.delete(`${BASE}/tasks/${taskId}`)).data;
  } catch (error) {
    return fail(error, "Failed to remove the service");
  }
};

/** { completedDate, km?, notes?, files?, allowLowerKm? } */
export const completeTask = async (taskId, payload) => {
  try {
    return (await axios.post(`${BASE}/tasks/${taskId}/complete`, payload)).data;
  } catch (error) {
    const question = lowerKm(error);
    if (question) throw question;
    return fail(error, "Failed to record the completion");
  }
};

export const reopenTask = async (taskId) => {
  try {
    return (await axios.post(`${BASE}/tasks/${taskId}/reopen`, {})).data;
  } catch (error) {
    return fail(error, "Failed to reopen");
  }
};

/** Upload files for a service and return their storage keys. A file that fails is reported and left out. */
export const uploadTaskFiles = async (taskId, files) => {
  const keys = [];
  for (const file of files) {
    try {
      const { data } = (await axios.post(`${BASE}/tasks/${taskId}/files/request-upload`, { fileName: file.name, contentType: file.type || 'application/octet-stream' })).data;
      const put = await fetch(data.presignedUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
      if (!put.ok) throw new Error(`Upload returned ${put.status}`);
      keys.push(data.key);
    } catch (error) {
      console.error(`Upload failed for ${file.name}`, error);
      toast.error(`${file.name} could not be uploaded`);
    }
  }
  return keys;
};

/** Attach an already uploaded file to a completed service (an invoice that arrived later). */
export const addTaskFile = async (taskId, key) => {
  try {
    return (await axios.post(`${BASE}/tasks/${taskId}/files`, { key })).data;
  } catch (error) {
    return fail(error, "Failed to attach the file");
  }
};

export const removeTaskFile = async (taskId, key) => {
  try {
    return (await axios.delete(`${BASE}/tasks/${taskId}/files`, { data: { key } })).data;
  } catch (error) {
    return fail(error, "Failed to remove the file");
  }
};

export const openTaskFile = async (key) => {
  try {
    const url = (await axios.post(`${BASE}/signed-urls`, { keys: [key] })).data.data?.[key];
    if (!url) throw new Error('This file is not available');
    window.open(url, '_blank', 'noopener');
  } catch (error) {
    fail(error, "Failed to open the file");
  }
};

export const getSuggestions = async (kind) => {
  try {
    return (await axios.get(`${BASE}/suggestions`, { params: { kind } })).data.data || { types: [], titles: [] };
  } catch (error) {
    console.error("Failed to load suggestions", error);
    return { types: [], titles: [] };
  }
};
